import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';
import AnonymousRibbon from '../assets/icons/anonymousRibbon.svg';
import Quote from '../assets/icons/quote3.svg';
import { Container, Heading, Text, colors, media } from '../ui-components';

/**
 * Component that renders a client quote
 *
 * @component ClientQuote
 * 
 * @param {object} props
 * 
 * @prop {object} quote
 * @prop {string} layout
 * 
 * @returns {JSX}
 */
const ClientQuote = props => {
  const { quote, layout } = props;
  const strippedQuote = quote?.quote?.replace(/^[\'"]+|[\'"]+$/g, '');
  const avatar = getImage(quote?.avatarData?.fluid);
  const image = getImage(quote?.imageData?.fluid);

  // Use image for specialized image
  if (layout == 'side-by-side') {
    return (
      <QuoteSectionContainer id={'quote-container'} sectionContainer>
        <QuoteContainer style={{ margin: '0px' }}>
          <QuoteIconImage src={Quote} alt="Quotation Mark Icon" />
          <QuoteHeading level={5}>{strippedQuote}</QuoteHeading>
        </QuoteContainer>

        <ClientAvatarContainer>
          {(image || avatar) && <ClientAvatar image={image || avatar} alt={quote.imageData?.title ?? 'Quote Author'} objectFit={'contain'}/>}
          <ClientName level={3}>{quote?.author}</ClientName>
          <ClientTitle size={'large'}>{quote.authorSubtitle}</ClientTitle>
        </ClientAvatarContainer>
      </QuoteSectionContainer>
    );
  }

  // Use avatar for regular square image
  return (
    <QuoteContainer className={'client-quote'}>
      <QuoteIconImage src={Quote} alt="Quotation Mark Icon" />
      <QuoteHeading level={5}>{strippedQuote}</QuoteHeading>
      <QuoteAuthorContainer>
        <QuoteAuthorTextContainer>
          <QuoteAuthorName size="large">{quote.author}</QuoteAuthorName>
          <QuoteAuthorTitle>{quote.authorSubtitle}</QuoteAuthorTitle>
        </QuoteAuthorTextContainer>
        {avatar ? (
          <QuoteAuthorImage image={avatar} alt={quote.avatarData?.title ?? 'Quote Author'} />
        ) : (
          <AnonymousAuthorImage src={AnonymousRibbon} alt="Anonymous Author" />
        )}
      </QuoteAuthorContainer>
    </QuoteContainer>
  );
};

const QuoteContainer = styled(Container)`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0;
  max-width: 504px;
  z-index: 1;

  @media screen and (max-width: ${media.mediumBottom}) {
    display: inline-flex;
    justify-content: flex-end;
    align-items: center;
    margin: 0;
  }
`;

const QuoteIconImage = styled.img`
  width: 163.136px;
  height: 120px;
  position: absolute;
  left: -117.625px;
  top: -43.965px;
  z-index: 2;

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 119px;
    height: 88px;
    position: static;
    margin: 0;
  }
`;

const QuoteHeading = styled(Heading)`
  align-self: stretch;
  color: ${colors.deepSmoke};
  font-weight: 400;
  padding: 40px 0 32px 0;
  z-index: 3;

  @media screen and (max-width: ${media.mediumBottom}) {
    text-align: center;
    padding: 0 0 28px 0;
    margin-top: -20px;
  }
`;

const QuoteAuthorContainer = styled(Container)`
  display: flex;
  margin: 0;
  padding: 0;

  @media screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
`;

const QuoteAuthorTextContainer = styled(Container)`
  margin-right: 0;
  padding: 0;
`;

const QuoteAuthorName = styled(Text)`
  text-align: right;

  @media screen and (max-width: ${media.mediumBottom}) {
    text-align: center;
    padding-bottom: 4px;
  }
`;

const QuoteAuthorTitle = styled(Text)`
  text-align: right;

  @media screen and (max-width: ${media.mediumBottom}) {
    text-align: center;
  }
`;

const QuoteAuthorImage = styled(GatsbyImage)`
  border-radius: 130.75px;
  height: 100px;
  width: 100px;
  margin: 0 0 0 28px;
  min-width: 100px;

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 80px;
    height: 80px;
    margin: 0 0 12px 0;
  }
`;

const AnonymousAuthorImage = styled.img`
  height: 100px;
  width: 100px;
  margin: 0 0 0 12px;
  min-width: 100px;

  @media screen and (max-width: ${media.mediumBottom}) {
    margin: 0;
  }
`;

const QuoteSectionContainer = styled(Container)`
  display: flex;
  justify-content: center;
  flex-direction: row;
  width: 100%;
  gap: 80px;
  align-items: center;

  @media screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column;
    gap: 0px;
  }
`;

const ClientAvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ClientAvatar = styled(GatsbyImage)`
  border-radius: 100%;
  height: 250px;
  width: 250px;
  margin-bottom: 20px;

  @media screen and (max-width: ${media.smallTop}) {
    height: 80px;
    width: 80px;
    margin-bottom: 12px;
  }
`;

const ClientName = styled(Heading)`
  margin-bottom: 4px;
  white-space: nowrap;
`;

const ClientTitle = styled(Text)``;

export default ClientQuote;
